import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useContext } from "react";
import { MessageContext } from "../../context/MessageContext";

import UserRoleUpdate from "./UserRoleUpdate";

import { IUserRole, IUserRoleListProps } from "./UserRole.model";
import { userRoleService } from "../../service/UserRole/userRole.service";
import UserRolePermission from "./UserRolePermission";

import { Can } from "../../ability/can";
import {
  PERMISSIONS_ACTION,
  PERMISSIONS_MODEL,
} from "../../library/utilities/constants/permissions";

const UserRoleList = ({
  openNew,
  loadUserRoles,
  data,
  loading,
}: IUserRoleListProps) => {
  const [roleDialog, setRoleDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<IUserRole | null>(null);
  const [permissionDialog, setPermissionDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { pushMessageForToast } = useContext(MessageContext);
  const deleteRole = (id: number) => {
    setDeleteId(id);
    setConfirmDialog(true);
  };

  const confirmDeleteRole = async () => {
    if (!deleteId) return;
    const response = await userRoleService.remove(deleteId);
    if (!response.success) {
      pushMessageForToast({
        severity: "error",
        message: response?.errors?.message,
      });
    }
    setConfirmDialog(false);
    loadUserRoles();
  };

  const actionBodyTemplate = (row: IUserRole) => {
    return (
      <div className="flex gap-2">
        <Can I={PERMISSIONS_ACTION.ROLE_CHANGE} a={PERMISSIONS_MODEL.ROLE}>
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={() => {
              setSelectedRole(row);
              setRoleDialog(true);
            }}
          />
        </Can>

        <Can I={PERMISSIONS_ACTION.ROLE_DELETE} a={PERMISSIONS_MODEL.ROLE}>
          <Button
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            onClick={() => deleteRole(row.id)}
          />
        </Can>

        <Button
          label="Permissions"
          icon="pi pi-lock"
          severity="warning"
          onClick={() => {
            setSelectedRole(row);
            setPermissionDialog(true);
          }}
        />
      </div>
    );
  };

  //TODO : We make it comman datatable for all with filter 
  const [globalFilter, setGlobalFilter] = useState("");
  const filteredData = data.filter((role: IUserRole) => {
    const search = globalFilter.toLowerCase();
    return role.name.toLowerCase().includes(search);
  });

  return (
    <>
      {/* Create / Update Dialog */}
      <Dialog
        visible={roleDialog}
        header="Update Role"
        modal
        onHide={() => setRoleDialog(false)}
        className="w-4">
        {selectedRole && (
          <UserRoleUpdate
            data={selectedRole}
            setUserRoleDialog={setRoleDialog}
            loadUserRoles={loadUserRoles}
          />
        )}
      </Dialog>

      <DataTable
        value={filteredData}
        loading={loading}
        dataKey="id"
        paginator
        rows={10}
        header={
          <div className="flex justify-content-between align-items-center gap-3">
            <h3>User Roles</h3>
            <div className="flex">
              <span className="p-input-icon-left mr-3">
                <i className="pi pi-search" />
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search by role name"
                  className="p-inputtext p-component"
                />
              </span>
              <Can I={PERMISSIONS_ACTION.ROLE_ADD} a={PERMISSIONS_MODEL.ROLE}>
                <Button label="New Role" onClick={openNew} />
              </Can>
            </div>
          </div>
        }>
        <Column sortable field="name" header="Role Name" />

        <Column header="Actions" body={actionBodyTemplate} />
      </DataTable>

      <ConfirmDialog
        visible={confirmDialog}
        onHide={() => setConfirmDialog(false)}
        message="Are you sure you want to delete this role?"
        header="Delete Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmDeleteRole}
        reject={() => setConfirmDialog(false)}
      />

      <Dialog
        visible={permissionDialog}
        header="Update Permission"
        maximized={true}
        modal
        appendTo="self"
        maximizable
        onHide={() => setPermissionDialog(false)}
        className="w-4">
        <UserRolePermission
          selectedRole={selectedRole}
          loadUserRoles={loadUserRoles}
          setPermissionDialog={setPermissionDialog}
        />
      </Dialog>
    </>
  );
};

export default UserRoleList;
